import {FormWindow, modules} from "@intermesh/groupoffice-core";
import {
	ArrayField,
	arrayfield,
	AutocompleteChips,
	autocompletechips,
	checkbox,
	checkboxselectcolumn,
	column,
	comp,
	Component,
	containerfield,
	datasourcestore,
	fieldset,
	t,
	table,
	tbar
} from "@intermesh/goui";
import {BoardFilter, projectbookDS, projectDS, supportlistDS, tasklistDS} from "./Index.js";

export class BoardFilterDialog extends FormWindow {
	private filterFields: Component[] = [];

	private readonly tasklistParentsField!: ArrayField;
	private tasklistChips!: AutocompleteChips;

	private readonly projectParentsField!: ArrayField;
	private projectChips!: AutocompleteChips;

	constructor() {
		super("Board");

		this.title = t("Backlog filters");
		this.width = 800;
		this.height = 700;

		this.stateId = "kanban-board-filter-dialog";

		this.modal = true;
		this.resizable = true;

		if (modules.isAvailable("community", "tasks")) {
			this.tasklistParentsField = arrayfield({
				name: "tasklistParents",
				hidden: true,
				buildField: () => containerfield({hidden: true})
			});

			this.projectParentsField = arrayfield({
				name: "projectParents",
				hidden: true,
				buildField: () => containerfield({hidden: true})
			});

			this.filterFields.push(
				containerfield({itemId: "Task", cls: "border-bottom"},
					tbar({},
						comp({tagName: "h4", html: t("Tasks")}),
						'->',
						checkbox({name: "enabled", value: true, type: "switch"}),
					),
					fieldset({},
						this.tasklistParentsField,
						this.projectParentsField,

						this.tasklistChips = autocompletechips({
							name: "tasklistParentsChips",
							label: t("Show only tasks belonging to these lists"),
							list: table({
								headers: false,
								store: datasourcestore({
									dataSource: tasklistDS,
									filters: {permission: {permissionLevel: 5}, role: {role: "list"}}
								}),
								rowSelectionConfig: {multiSelect: true, clickToAdd: true},
								columns: [checkboxselectcolumn(), column({id: "name", sortable: true, resizable: true})]
							}),
							listeners: {
								autocomplete: ({target, input}) => {
									target.list.store.setFilter("autocomplete", {text: input});
									void target.list.store.load();
								},
								setvalue: ({newValue}) => {
									this.tasklistParentsField.value = newValue;
								}
							},
							chipRenderer: async (chip, record) => {
								const tasklist = await tasklistDS.single(record.entityId);
								chip.text = tasklist.name;
							},
							valueToKey: v => v.entity + '-' + v.entityId,
							pickerRecordToValue(field, record): any {
								return {entity: "TaskList", entityId: record.id};
							}
						}),
						this.projectChips = autocompletechips({
							name: "projectParentsChips",
							label: t("Show only tasks belonging to these projects"),
							list: table({
								headers: false,
								store: datasourcestore({
									dataSource: projectDS,
									filters: {permission: {permissionLevel: 5}}
								}),
								rowSelectionConfig: {multiSelect: true, clickToAdd: true},
								columns: [checkboxselectcolumn(), column({id: "name", sortable: true, resizable: true})]
							}),
							listeners: {
								autocomplete: ({target, input}) => {
									target.list.store.setFilter("autocomplete", {text: input});
									void target.list.store.load();
								},
								setvalue: ({newValue}) => {
									this.projectParentsField.value = newValue ?? [];
								}
							},
							chipRenderer: async (chip, record) => {
								const project = await projectDS.single(record.entityId);
								chip.text = project.name;
							},
							pickerRecordToValue(field, record): any {
								return {entity: "Project3", entityId: record.id};
							}
						})
					)
				)
			);
		}

		if (modules.isAvailable("business", "support")) {
			this.filterFields.push(
				containerfield({itemId: "SupportTicket", cls: "border-bottom"},
					tbar({},
						comp({tagName: "h4", html: t("Support tickets")}),
						'->',
						checkbox({name: "enabled", value: true, type: "switch"}),
					),
					fieldset({},
						autocompletechips({
							name: "parents",
							label: t("Show only tickets belonging to these lists"),
							list: table({
								headers: false,
								store: datasourcestore({
									dataSource: supportlistDS,
									filters: {permission: {permissionLevel: 5}, role: {role: "support"}}
								}),
								rowSelectionConfig: {multiSelect: true, clickToAdd: true},
								columns: [
									checkboxselectcolumn(),
									column({id: "name", sortable: true, resizable: true})
								]
							}),
							listeners: {
								autocomplete: ({target, input}) => {
									target.list.store.setFilter("autocomplete", {text: input});
									void target.list.store.load();
								}
							},
							chipRenderer: async (chip, record) => {
								const tasklist = await tasklistDS.single(record.entityId);
								chip.text = tasklist.name;
							},
							valueToKey: v => v.entity + '-' + v.entityId,
							pickerRecordToValue(field, record): any {
								return {entity: "SupportList", entityId: record.id};
							}
						})
					)
				)
			)
		}

		if (modules.isAvailable("business", "projects3")) {
			this.filterFields.push(
				containerfield({itemId: "Project3"},
					tbar({},
						comp({tagName: "h4", html: t("Projects")}),
						'->',
						checkbox({name: "enabled", type: "switch", value: true}),
					),
					fieldset({},
						autocompletechips({
							name: "parents",
							label: t("Show only projects belonging to these project books"),
							list: table({
								headers: false,
								store: datasourcestore({
									dataSource: projectbookDS,
									filters: {permission: {permissionLevel: 5}}
								}),
								rowSelectionConfig: {multiSelect: true, clickToAdd: true},
								columns: [
									checkboxselectcolumn(),
									column({id: "name", sortable: true, resizable: true})
								]
							}),
							listeners: {
								autocomplete: ({target, input}) => {
									target.list.store.setFilter("autocomplete", {text: input});
									void target.list.store.load();
								}
							},
							chipRenderer: async (chip, record) => {
								const projectbook = await projectbookDS.single(record.entityId);
								chip.text = projectbook.name;
							},
							valueToKey: v => v.entity + '-' + v.entityId,
							pickerRecordToValue(field, record): any {
								return {entity: "Project3", entityId: record.id};
							}
						})
					)
				)
			)
		}

		this.generalTab.items.add(...this.filterFields);

		this.form.on("load", ({data}) => {
			this.filterFields.forEach((field) => {
				const entity = (field as any).itemId;
				const filter = data.filters.find((f: BoardFilter) => f.entity === entity);
				if (!filter) return;

				if (entity === "Task") {
					const tasklistParents = filter.parents.filter((p: any) => p.entity === "TaskList");
					const projectParents = filter.parents.filter((p: any) => p.entity === "Project3");

					this.tasklistParentsField.value = tasklistParents;
					this.projectParentsField.value = projectParents;

					const applyValues = () => {
						this.tasklistChips.value = tasklistParents;
						this.projectChips.value = projectParents;
					};

					if (this.tasklistChips.rendered) {
						applyValues();
					} else {
						this.tasklistChips.on("render", applyValues, {once: true});
					}
				} else {
					(field as any).value = filter;
				}
			});
		});

		this.form.on("beforesave", ({data}) => {
			data.filters = this.filterFields.map((field) => {
				const entity = (field as any).itemId;
				const {id, enabled, tasklistParents = [], projectParents = [], parents = []} = (field as any).value;

				return {
					id,
					entity,
					enabled,
					parents: entity === "Task" ? [...tasklistParents, ...projectParents] : parents
				};
			});
		});
	}
}